"use client";
import { z } from "zod";
import { useState } from "react";
import axios from "@config/axios";
import { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { Input } from "@components/ui/input";
import { toast } from "@components/ui/toaster";
import { Button } from "@components/ui/button";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, Mail, Send } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter, useSearchParams } from "next/navigation";
import { ForgotPasswordSchema } from "@schemas/forgotPassword";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@components/ui/form";

const ForgotPasswordForm = () => {
	const router = useRouter();
	const searchParams = useSearchParams();
	const urlParams = new URLSearchParams(searchParams?.toString() || "");
	const [onSendToast, setOnSendToast] = useState<string | number>();

	const defaultValues: z.infer<typeof ForgotPasswordSchema> = {
		email: "",
	};

	const form = useForm<z.infer<typeof ForgotPasswordSchema>>({
		defaultValues: defaultValues,
		mode: "onTouched",
		resolver: zodResolver(ForgotPasswordSchema),
	});

	const onSend = async (values: z.infer<typeof ForgotPasswordSchema>) => {
		setOnSendToast(toast.loading("Loading...", { description: "Sending OTP..." }));
		const { email } = values;
		await axios.post("/api/otp/get-otp", { email });
	};

	const { mutate, isPending } = useMutation({
		mutationFn: onSend,
		onSuccess: (_: void, values: z.infer<typeof ForgotPasswordSchema>) => {
			const { email } = values;
			toast.success("Success!", { id: onSendToast, description: "OTP sent successfully!" });
			urlParams.set("email", email);
			urlParams.set("type", "FORGOT_PASSWORD");
			router.push(`/auth/verify${urlParams ? `?${urlParams.toString()}` : ""}`);
		},
		onError: (error: unknown) => {
			toast.error("Error!", { id: onSendToast, description: (error as AxiosError<string>)?.response?.data || "Internal server error!" });
		},
	});

	return (
		<Form {...form}>
			<form className="flex h-full w-full max-w-sm flex-col items-center justify-stretch gap-7" onSubmit={form.handleSubmit(values => mutate(values))}>
				<div className="flex w-full flex-col items-center justify-center gap-6">
					<FormField
						control={form.control}
						name="email"
						render={({ field }) => (
							<FormItem className="w-full">
								<FormLabel>
									Email<span className="text-destructive">*</span>
								</FormLabel>
								<FormControl>
									<Input placeholder="Enter your email" startContent={<Mail size={16} />} {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<div className="flex w-full flex-col items-center justify-between gap-3">
					<Button className="w-36 justify-between" disabled={isPending} size="lg" type="submit" variant="default">
						Send
						<Send size={16} />
					</Button>
					<Button className="w-36" disabled={isPending} onClick={router.back} size="lg" type="button" variant="ghost">
						<ArrowLeft size={16} />
						Back
					</Button>
				</div>
			</form>
		</Form>
	);
};

export default ForgotPasswordForm;
